import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import DualDatePicker from '../../components/common/DualDatePicker';
import toast from 'react-hot-toast';
import {
  MdAdd, MdPeople, MdClose, MdCheckCircle,
  MdCancel, MdPlayArrow, MdAccessTime,
  MdEdit, MdSwapHoriz, MdDelete,
  MdViewList, MdCalendarViewMonth, MdChevronLeft, MdChevronRight,
  MdFilterList,
} from 'react-icons/md';
import { FaUserTie } from 'react-icons/fa';
import RecognizeModal from '../../components/common/RecognizeModal';
import AttendanceModal from '../../components/attendance/AttendanceModal';
import * as sessionService from '../../services/sessionService';
import { getClasses } from '../../services/classService';
import { getTrainers } from '../../services/trainerService';
import useAuth from '../../hooks/useAuth';
import useEscapeKey from '../../hooks/useEscapeKey';
import { getBeltStyle } from '../../utils/beltStyle';
import useImpersonation from '../../hooks/useImpersonation';
import useClubVocab from '../../hooks/useClubVocab';
import { useCalendar } from '../../context/CalendarContext';
import useTheme from '../../context/ThemeContext';
import { adToBs, bsToAd, getDaysInBsMonth, BS_MONTHS, isoLocalDay, ktmDayKey } from '../../utils/calendar';
import DualDate from '../../components/common/DualDate';
import { getNepalHoliday } from '../../data/nepalHolidays';
import SuspendedAccessPanel from '../../components/common/SuspendedAccessPanel';
import { isClubSuspended, isClubCancelled } from '../../utils/clubError';

// ── Helpers ───────────────────────────────────────────────────────────────────
const TODAY = new Date().toISOString().slice(0, 10);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtDateNice = (str) => {
  if (!str) return '';
  const d = new Date(str + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const fmtClock = (d) =>
  d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—';

// Local-calendar "YYYY-MM-DD" (avoids the UTC day-shift of toISOString in +tz).
const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// "HH:mm" → { hm: "6:00", ap: "AM" } for the agenda time block.
const splitTime = (t) => {
  if (!t) return { hm: '—', ap: '' };
  const [h, m] = t.split(':').map(Number);
  return { hm: `${h % 12 || 12}:${String(m).padStart(2, '0')}`, ap: h >= 12 ? 'PM' : 'AM' };
};

const isToday = (d) => {
  if (!d) return false;
  const s = new Date(d), n = new Date();
  return s.getFullYear() === n.getFullYear() && s.getMonth() === n.getMonth() && s.getDate() === n.getDate();
};

const isYesterday = (d) => {
  if (!d) return false;
  const s = new Date(d), y = new Date();
  y.setDate(y.getDate() - 1);
  return s.getFullYear() === y.getFullYear() && s.getMonth() === y.getMonth() && s.getDate() === y.getDate();
};


const STATUS_STYLE = {
  active:    'bg-green-100 text-green-700',
  completed: 'bg-nav-hover text-muted',
  cancelled: 'bg-red-100 text-red-500',
};

// "YYYY-MM" AD month key for a JS Date (used to fetch calendar sessions).
function adMonthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// "Sep – Oct 2026" range covering the AD months a BS month straddles.
function adRangeLabel(firstAd, lastAd) {
  const mo1 = firstAd.toLocaleDateString('en-US', { month: 'short' });
  const mo2 = lastAd.toLocaleDateString('en-US',  { month: 'short' });
  const y1  = firstAd.getFullYear();
  const y2  = lastAd.getFullYear();
  if (mo1 === mo2 && y1 === y2) return `${mo1} ${y1}`;
  if (y1 === y2)                return `${mo1} – ${mo2} ${y2}`;
  return `${mo1} ${y1} – ${mo2} ${y2}`;
}

// ── Calendar View (Hamro Patro–style Bikram Sambat month grid) ────────────────
function CalendarView({ classFilter, onPickDay }) {
  const { currentCalendar } = useCalendar();
  const adMode = currentCalendar === 'ad';

  // View is held in BS. Sessions stay keyed on their real AD date everywhere.
  const todayBs = adToBs(new Date());
  const [bsYear,  setBsYear]  = useState(todayBs.year);
  const [bsMonth, setBsMonth] = useState(todayBs.month);

  const daysInMonth = getDaysInBsMonth(bsYear, bsMonth);
  const firstAd     = useMemo(() => bsToAd(bsYear, bsMonth, 1),           [bsYear, bsMonth]);
  const lastAd      = useMemo(() => bsToAd(bsYear, bsMonth, daysInMonth), [bsYear, bsMonth, daysInMonth]);
  const offset      = (firstAd.getDay() + 6) % 7; // Mon-start: Sun→6, Mon→0

  // Each BS day → its derived AD date. Sessions match on this real AD date.
  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < offset; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const ad = bsToAd(bsYear, bsMonth, d);
      arr.push({
        bsDay:   d,
        ad,
        // `ad` is a LOCAL-midnight Date (bsToAd output) → read local getters, not toISOString
        // (which shifts to the previous day on any browser ahead of UTC, i.e. all of Nepal).
        dateStr: isoLocalDay(ad),
        isSat:   ad.getDay() === 6,
        holiday: getNepalHoliday(bsYear, bsMonth, d),
      });
    }
    while (arr.length % 7 !== 0) arr.push(null);
    const weeks = [];
    for (let i = 0; i < arr.length; i += 7) weeks.push(arr.slice(i, i + 7));
    return weeks;
  }, [bsYear, bsMonth, daysInMonth, offset]);

  // A BS month straddles two AD months. The backend slices by a single AD month,
  // so fetch both overlapping AD months and merge — same fetch, no logic change.
  const adMonth1 = adMonthKey(firstAd);
  const adMonth2 = adMonthKey(lastAd);
  const twoMonths = adMonth2 !== adMonth1;

  const q1 = useQuery({
    queryKey: ['calendar-sessions', adMonth1, classFilter],
    queryFn:  () => sessionService.getCalendarSessions({ month: adMonth1, classId: classFilter || undefined }),
    staleTime: 30000,
  });
  const q2 = useQuery({
    queryKey: ['calendar-sessions', adMonth2, classFilter],
    queryFn:  () => sessionService.getCalendarSessions({ month: adMonth2, classId: classFilter || undefined }),
    enabled:   twoMonths,
    staleTime: 30000,
  });
  const isLoading = q1.isLoading || (twoMonths && q2.isLoading);

  const sessions = useMemo(() => {
    const byId = new Map();
    (q1.data?.sessions || []).forEach(s => byId.set(s._id, s));
    if (twoMonths) (q2.data?.sessions || []).forEach(s => byId.set(s._id, s));
    return [...byId.values()];
  }, [q1.data, q2.data, twoMonths]);

  const dayMap = useMemo(() => {
    const map = {};
    sessions.forEach(s => {
      const day = ktmDayKey(s.date); // s.date is a stored (UTC-midnight) timestamp → Kathmandu day
      if (!map[day]) map[day] = [];
      map[day].push(s);
    });
    return map;
  }, [sessions]);

  const prevMonth = () => {
    if (bsMonth === 1) { setBsYear(y => y - 1); setBsMonth(12); }
    else               { setBsMonth(mo => mo - 1); }
  };
  const nextMonth = () => {
    if (bsMonth === 12) { setBsYear(y => y + 1); setBsMonth(1); }
    else                { setBsMonth(mo => mo + 1); }
  };

  const DAY_NAMES   = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const bsMonthName = BS_MONTHS[bsMonth - 1];

  const chevronCls = 'w-8 h-8 rounded-full border border-divider bg-card text-secondary hover:bg-secondary hover:text-card hover:border-secondary flex items-center justify-center transition-all duration-200';

  return (
    <div className="rounded-lg bg-card overflow-hidden border border-divider shadow-sm">
      {/* Month nav */}
      <div className="flex items-center justify-between px-4 py-3.5">
        <button onClick={prevMonth} className={chevronCls} aria-label="Previous month">
          <MdChevronLeft size={18} />
        </button>
        <div className="text-center">
          <h2 className="text-base font-semibold text-secondary leading-tight">{bsYear} {bsMonthName}</h2>
          <p className="text-[11px] text-muted mt-0.5">{adRangeLabel(firstAd, lastAd)}</p>
        </div>
        <button onClick={nextMonth} className={chevronCls} aria-label="Next month">
          <MdChevronRight size={18} />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 border-t border-divider">
        {DAY_NAMES.map((d, i) => (
          <div key={d} className={`py-2 text-center text-[11px] font-medium ${i === 5 ? 'text-subtle' : 'text-muted'}`}>
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="h-64 flex items-center justify-center text-muted text-sm">Loading calendar…</div>
      ) : (
        <div>
          {cells.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7">
              {week.map((cell, di) => {
                if (!cell) {
                  return <div key={di} className="min-h-[84px] border-b border-r border-divider bg-nav-hover" />;
                }
                const { bsDay, ad, dateStr, isSat, holiday } = cell;
                const daySessions = dayMap[dateStr] || [];
                const isTodayCell = bsYear === todayBs.year && bsMonth === todayBs.month && bsDay === todayBs.day;
                const isRed       = !!holiday || isSat;

                // Primary = BS day (Hamro Patro). In AD mode, flip: AD day big, BS day small.
                const primaryNum   = adMode ? ad.getDate() : bsDay;
                const secondaryNum = adMode
                  ? bsDay
                  : ad.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

                return (
                  <div
                    key={di}
                    onClick={() => onPickDay(dateStr)}
                    className={`relative min-h-[84px] p-2 flex flex-col border-b border-r border-divider cursor-pointer hover:bg-nav-hover transition-colors
                      ${holiday ? 'bg-rose-500/10' : isSat ? 'bg-nav-hover' : ''}`}
                  >
                    {/* Top row: primary number (left) + secondary date (right) */}
                    <div className="flex items-start justify-between gap-1">
                      {isTodayCell ? (
                        <span className="w-[26px] h-[26px] -ml-0.5 -mt-0.5 flex items-center justify-center rounded-full bg-rose-500 text-white text-sm font-semibold shadow-sm">
                          {primaryNum}
                        </span>
                      ) : (
                        <span className={`text-sm font-medium leading-none mt-0.5 ${isRed ? 'text-rose-500' : 'text-secondary'}`}>
                          {primaryNum}
                        </span>
                      )}
                      <span className="text-[10px] text-muted leading-none mt-0.5">{secondaryNum}</span>
                    </div>

                    {/* Bottom: holiday chip, then session pill(s) */}
                    <div className="mt-auto space-y-1 pt-1">
                      {holiday && (
                        <div className="flex items-center gap-1" title={holiday}>
                          <span className="w-[5px] h-[5px] rounded-full bg-rose-400 flex-shrink-0" />
                          <span className="text-[10px] leading-tight text-rose-600 truncate">{holiday}</span>
                        </div>
                      )}
                      {daySessions.slice(0, 2).map(s => (
                        <div
                          key={s._id}
                          title={s.className}
                          className={`flex items-center gap-0.5 bg-blue-100 text-blue-700 rounded px-1.5 py-0.5 text-[10px] font-medium leading-tight truncate
                            ${s.status === 'cancelled' ? 'opacity-40 line-through' : ''}`}
                        >
                          <MdCheckCircle size={9} className="flex-shrink-0" />
                          <span className="truncate">{s.className}</span>
                        </div>
                      ))}
                      {daySessions.length > 2 && (
                        <div className="text-[10px] text-muted px-0.5 font-medium">+{daySessions.length - 2} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-2.5 border-t border-divider text-[11px] text-muted">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-rose-500" /> Public holiday
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Session
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-rose-500" /> Today
        </span>
      </div>
    </div>
  );
}

// ── Create Session Modal ──────────────────────────────────────────────────────
function CreateModal({ onClose, onCreated, defaultClassId = '', defaultDate = '' }) {
  const { isReadOnly } = useImpersonation();
  const currentTheme = useTheme()?.currentTheme;
  useEscapeKey(onClose);
  const [showPicker, setShowPicker] = useState(false);
  const { register, control, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      date:    defaultDate || TODAY,
      classId: defaultClassId,
    },
  });

  const { data: classesData } = useQuery({
    queryKey: ['classes-list'],
    queryFn:  () => getClasses({ limit: 100 }).then(r => r.data),
    staleTime: 60000,
  });
  const classes = classesData?.classes || [];

  const { mutate, isPending } = useMutation({
    mutationFn: sessionService.createSession,
    onSuccess: (data) => { toast.success('Session created'); onCreated(data.session); },
    onError:   (e)    => toast.error(e.response?.data?.message || 'Failed to create session'),
  });

  const onSubmit = (values) => {
    const payload = { ...values };
    if (!payload.classId) delete payload.classId;
    mutate(payload);
  };

  // Date summary (BS primary · AD secondary) — same styling as the agenda navigator.
  const dateVal = watch('date') || TODAY;
  let bsPrimary = '', adSecondary = '';
  try {
    const bs = adToBs(new Date(dateVal + 'T00:00:00'));
    bsPrimary   = `${bs.monthName} ${bs.day}, ${bs.year}`;
    adSecondary = new Date(dateVal + 'T00:00:00')
      .toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  } catch { /* leave blank if unparseable */ }

  const inputCls = 'w-full px-3 py-2.5 rounded-xl text-sm bg-card border border-divider text-secondary placeholder-subtle focus:outline-none focus:border-secondary transition-colors';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card border border-divider rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-divider">
          <div>
            <h2 className="font-semibold text-secondary">New Session</h2>
            <p className="text-xs text-muted mt-0.5">
              Create a one-off session. Regular classes appear automatically on their scheduled days.
            </p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-secondary flex-shrink-0"><MdClose size={20} /></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          {/* Session name */}
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Session name *</label>
            <input
              {...register('className', { required: 'Required' })}
              placeholder="e.g. Extra sparring session"
              className={`${inputCls} ${errors.className ? 'border-rose-400/70' : ''}`}
            />
            {errors.className && <p className="text-rose-500 text-xs mt-1">{errors.className.message}</p>}
          </div>

          {/* Link to class */}
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Link to class <span className="text-muted">(optional)</span></label>
            <select
              {...register('classId')}
              className="w-full px-3 py-2.5 rounded-xl text-sm bg-card border border-divider text-secondary focus:outline-none focus:border-secondary transition-colors"
            >
              <option value="">— No class link —</option>
              {classes.map(c => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Date summary + collapsible picker */}
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Date</label>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-divider bg-card px-3.5 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-secondary leading-tight truncate">{bsPrimary || '—'}</p>
                {adSecondary && <p className="text-[11px] text-muted mt-0.5 truncate">{adSecondary}</p>}
              </div>
              <button
                type="button"
                onClick={() => setShowPicker(v => !v)}
                className="text-xs font-medium text-secondary hover:opacity-70 transition-colors flex-shrink-0"
              >
                {showPicker ? 'Done' : 'Change date'}
              </button>
            </div>
            {showPicker && (
              <div className="mt-2 bg-card rounded-xl p-3">
                <Controller
                  name="date"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <DualDatePicker
                      label="Date"
                      compact
                      value={field.value || ''}
                      onChange={field.onChange}
                      error={errors.date?.message}
                    />
                  )}
                />
              </div>
            )}
          </div>

          {/* Times */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Start time</label>
              <input type="time" {...register('startTime')} style={{ colorScheme: 'dark' }} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">End time</label>
              <input type="time" {...register('endTime')} style={{ colorScheme: 'dark' }} className={inputCls} />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-full text-sm text-secondary border border-divider hover:bg-nav-hover transition-colors">Cancel</button>
            <button type="submit" disabled={isPending || isReadOnly} title={isReadOnly ? 'Disabled: read-only support mode' : undefined} className={`flex-1 py-2.5 rounded-full text-sm font-medium ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}>
              {isPending ? 'Creating…' : 'Create Session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Substitute Trainer Modal ──────────────────────────────────────────────────
function SubstituteModal({ session, onClose, onDone }) {
  const { isReadOnly } = useImpersonation();
  const currentTheme = useTheme()?.currentTheme;
  useEscapeKey(onClose);
  const [selected, setSelected] = useState(session.substitutedBy?._id || session.substitutedBy || '');
  const { formatDate } = useCalendar();

  const { data, isLoading } = useQuery({
    queryKey: ['trainers-list'],
    queryFn:  () => getTrainers({ limit: 50 }).then(r => r.data),
    staleTime: 60000,
  });
  const trainers = data?.trainers || [];

  const { mutate, isPending } = useMutation({
    mutationFn: () => sessionService.substituteTrainer(session._id, selected || null),
    onSuccess: () => { toast.success('Trainer updated'); onDone(); onClose(); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to update trainer'),
  });

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider">
          <div>
            <h2 className="font-semibold text-secondary">Replace Trainer</h2>
            <p className="text-xs text-subtle mt-0.5">{session.className} · <DualDate date={session.date} /></p>
          </div>
          <button onClick={onClose} className="text-subtle hover:text-secondary"><MdClose size={20} /></button>
        </div>

        <div className="p-5 space-y-3">
          <div>
            <p className="text-xs font-medium text-muted mb-1">Original Trainer</p>
            <p className="text-sm font-semibold text-secondary">{session.trainerId?.name || 'Unknown'}</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted mb-1">Substitute Trainer</label>
            {isLoading ? (
              <div className="h-10 bg-nav-hover animate-pulse rounded-xl" />
            ) : (
              <select
                value={selected}
                onChange={e => setSelected(e.target.value)}
                className="w-full px-3 py-2.5 border border-divider rounded-xl text-sm focus:outline-none focus:border-secondary bg-card"
              >
                <option value="">— No substitute (clear) —</option>
                {trainers.map(t => {
                  const id   = t.user?._id || t._id;
                  const name = t.user?.name || t.fullName || 'Trainer';
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>
            )}
          </div>

          {selected && (
            <p className="text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded-xl">
              The substitute trainer will be able to see and take attendance for this session.
            </p>
          )}
        </div>

        <div className="flex gap-3 px-5 pb-5">
          <button onClick={onClose} className="flex-1 py-2.5 border border-divider rounded-full text-sm text-secondary hover:bg-nav-hover transition-colors">Cancel</button>
          <button onClick={() => mutate()} disabled={isPending || isReadOnly} title={isReadOnly ? 'Disabled: read-only support mode' : undefined} className={`flex-1 py-2.5 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}>
            {isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Session Modal ────────────────────────────────────────────────────────
function EditModal({ session, onClose, onSaved }) {
  const { isReadOnly } = useImpersonation();
  const currentTheme = useTheme()?.currentTheme;
  useEscapeKey(onClose);
  const { formatDate } = useCalendar();
  const { register, handleSubmit } = useForm({
    defaultValues: {
      startTime: session.startTime || '',
      endTime:   session.endTime   || '',
      classId:   session.classId?._id || session.classId || '',
      trainerId: session.trainerId?._id || session.trainerId || '',
      notes:     session.notes || '',
    },
  });

  const { data: classesData } = useQuery({
    queryKey: ['classes-list'],
    queryFn:  () => getClasses({ limit: 100 }).then(r => r.data),
    staleTime: 60000,
  });
  const classes = classesData?.classes || [];

  const { data: trainersData } = useQuery({
    queryKey: ['trainers-list'],
    queryFn:  () => getTrainers({ limit: 50 }).then(r => r.data),
    staleTime: 60000,
  });
  const trainers = trainersData?.trainers || [];

  const { mutate, isPending } = useMutation({
    mutationFn: (values) => sessionService.updateSession(session._id, values),
    onSuccess: () => { toast.success('Session updated'); onSaved(); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to update session'),
  });

  const onSubmit = (values) => {
    const payload = { ...values };
    if (!payload.classId)   payload.classId   = null;
    if (!payload.trainerId) delete payload.trainerId;
    mutate(payload);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-divider">
          <div>
            <h2 className="font-semibold text-secondary">Edit Session</h2>
            <p className="text-xs text-subtle mt-0.5">{session.className} · <DualDate date={session.date} /></p>
          </div>
          <button onClick={onClose} className="text-subtle hover:text-secondary"><MdClose size={20} /></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Start Time</label>
              <input type="time" {...register('startTime')} className="w-full px-3 py-2.5 border border-divider rounded-xl text-sm focus:outline-none focus:border-secondary" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">End Time</label>
              <input type="time" {...register('endTime')} className="w-full px-3 py-2.5 border border-divider rounded-xl text-sm focus:outline-none focus:border-secondary" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted mb-1">Class</label>
            <select {...register('classId')} className="w-full px-3 py-2.5 border border-divider rounded-xl text-sm focus:outline-none focus:border-secondary bg-card">
              <option value="">— No class link —</option>
              {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted mb-1">Trainer</label>
            <select {...register('trainerId')} className="w-full px-3 py-2.5 border border-divider rounded-xl text-sm focus:outline-none focus:border-secondary bg-card">
              <option value="">— No change —</option>
              {trainers.map(t => {
                const id   = t.user?._id || t._id;
                const name = t.user?.name || t.fullName || 'Trainer';
                return <option key={id} value={id}>{name}</option>;
              })}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted mb-1">Notes</label>
            <textarea
              {...register('notes')}
              rows={3}
              placeholder="Optional notes about this session…"
              className="w-full px-3 py-2.5 border border-divider rounded-xl text-sm focus:outline-none focus:border-secondary resize-none"
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-divider rounded-full text-sm text-secondary hover:bg-nav-hover transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={isPending || isReadOnly} title={isReadOnly ? 'Disabled: read-only support mode' : undefined} className={`flex-1 py-2.5 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}>
              {isPending ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ── Session Detail Side Panel ─────────────────────────────────────────────────
function SessionDetail({ session, isAdmin, onClose, onRefresh, onSubstitute, onTakeAttendance, onEdit }) {
  const { isReadOnly } = useImpersonation();
  const currentTheme = useTheme()?.currentTheme;
  const v = useClubVocab();
  const qc = useQueryClient();
  const [confirmAction, setConfirmAction] = useState(null);
  const { formatDateNice } = useCalendar();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['session-attendance', session._id],
    queryFn:  () => sessionService.getSessionAttendance(session._id),
  });
  const records = data?.records || [];
  const present = records.filter(r => (r.status || 'present') === 'present').length;
  const late    = records.filter(r => r.status === 'late').length;
  const absent  = records.filter(r => r.status === 'absent').length;

  const { mutate: setStatus } = useMutation({
    mutationFn: (s) => sessionService.updateStatus(session._id, s),
    onSuccess:  () => { toast.success('Status updated'); qc.invalidateQueries(['sessions']); onRefresh(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to update status'),
  });

  const { mutate: doCancel, isPending: cancelling } = useMutation({
    mutationFn: () => sessionService.cancelSession(session._id),
    onSuccess:  () => { toast.success('Session cancelled'); qc.invalidateQueries(['sessions']); onClose(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to cancel session'),
  });

  const { mutate: doDelete, isPending: deleting } = useMutation({
    mutationFn: () => sessionService.deleteSession(session._id),
    onSuccess:  () => { toast.success('Session deleted'); qc.invalidateQueries(['sessions']); onClose(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to delete session'),
  });

  const canTakeAttendance = session.status !== 'cancelled' && (
    isAdmin ? (isToday(session.date) || isYesterday(session.date)) : isToday(session.date)
  );

  const substituteName = session.substitutedBy?.name;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-card shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-divider flex-shrink-0">
          <div>
            <h2 className="font-bold text-secondary">{session.className}</h2>
            <p className="text-xs text-muted mt-0.5">
              {formatDateNice(session.date || '')}
              {session.startTime && ` · ${fmtTime(session.startTime)}`}
              {session.endTime   && ` – ${fmtTime(session.endTime)}`}
              {session.classId?.name && <span className="ml-1 text-primary font-medium"> · {session.classId.name}</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-subtle hover:text-secondary ml-4"><MdClose size={20} /></button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          {/* Take Attendance */}
          {canTakeAttendance && (
            <button
              onClick={onTakeAttendance}
              disabled={isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
              className={`w-full flex items-center justify-center gap-2 py-4 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full font-semibold text-base hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <MdEdit size={20} /> Take Attendance
            </button>
          )}
          {!canTakeAttendance && session.status !== 'cancelled' && (
            <div className="w-full flex items-center gap-2 py-3 px-4 bg-nav-hover border border-divider rounded-2xl text-sm text-muted">
              <MdAccessTime size={16} />
              {isAdmin
                ? 'Attendance can be marked on the day or the day after.'
                : 'Attendance can only be marked on the day of the session.'}
            </div>
          )}

          {/* Trainer info */}
          <div className="bg-nav-hover rounded-2xl p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <FaUserTie className="text-subtle text-base flex-shrink-0" />
              <div>
                <p className="text-xs text-subtle">Trainer</p>
                <p className="text-sm font-semibold text-secondary">{session.trainerId?.name || 'Unknown'}</p>
                {substituteName && (
                  <p className="text-xs text-blue-500 mt-0.5 flex items-center gap-1">
                    <MdSwapHoriz size={14} /> Substitute: <strong>{substituteName}</strong>
                  </p>
                )}
              </div>
            </div>
            {isAdmin && (
              <button
                onClick={onSubstitute}
                disabled={isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className="flex items-center gap-1.5 text-xs text-primary font-medium px-3 py-1.5 rounded-lg bg-primary-light hover:bg-primary/10 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MdSwapHoriz size={15} /> Replace
              </button>
            )}
          </div>

          {/* Status row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLE[session.status]}`}>
              {session.status}
            </span>
            {session.status === 'active' && (
              <button onClick={() => setStatus('completed')} disabled={isReadOnly} title={isReadOnly ? 'Disabled: read-only support mode' : undefined} className="flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <MdCheckCircle size={13} /> Complete
              </button>
            )}
            {session.status === 'completed' && (
              <button onClick={() => setStatus('active')} disabled={isReadOnly} title={isReadOnly ? 'Disabled: read-only support mode' : undefined} className="flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-green-50 text-green-600 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <MdPlayArrow size={13} /> Reopen
              </button>
            )}
          </div>

          {/* Admin actions */}
          {isAdmin && (
            <div>
              {confirmAction === null && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={onEdit}
                    disabled={isReadOnly}
                    title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl bg-nav-hover text-secondary hover:bg-nav-hover border border-divider transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <MdEdit size={14} /> Edit
                  </button>
                  {session.status !== 'cancelled' && (
                    <button
                      onClick={() => setConfirmAction('cancel')}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                      className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <MdCancel size={14} /> Cancel Session
                    </button>
                  )}
                  <button
                    onClick={() => setConfirmAction('delete')}
                    disabled={isReadOnly}
                    title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 border border-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <MdDelete size={14} /> Delete
                  </button>
                </div>
              )}

              {confirmAction === 'cancel' && (
                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4">
                  <p className="font-semibold text-orange-800 text-sm mb-1">Cancel this session?</p>
                  <p className="text-xs text-orange-600 mb-3">{v.members} will not be marked absent.</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmAction(null)}
                      className="flex-1 py-2 border border-orange-200 rounded-xl text-xs text-orange-700 hover:bg-orange-100 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => doCancel()}
                      disabled={cancelling || isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                      className="flex-1 py-2 bg-orange-500 text-white rounded-xl text-xs font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {cancelling ? 'Cancelling…' : 'Yes, Cancel Session'}
                    </button>
                  </div>
                </div>
              )}

              {confirmAction === 'delete' && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                  {session.attendanceTaken ? (
                    <>
                      <p className="font-semibold text-red-700 text-sm mb-1">Cannot delete</p>
                      <p className="text-xs text-red-600 mb-3">Attendance has been recorded. Cancel instead.</p>
                      <button
                        onClick={() => setConfirmAction(null)}
                        className="w-full py-2 border border-red-200 rounded-xl text-xs text-red-700 hover:bg-red-100 transition-colors"
                      >
                        Back
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-red-700 text-sm mb-1">Delete this session?</p>
                      <p className="text-xs text-red-500 mb-3">This will permanently delete the session.</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setConfirmAction(null)}
                          className="flex-1 py-2 border border-red-200 rounded-xl text-xs text-red-700 hover:bg-red-100 transition-colors"
                        >
                          Back
                        </button>
                        <button
                          onClick={() => doDelete()}
                          disabled={deleting || isReadOnly}
                          title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                          className="flex-1 py-2 bg-red-500 text-white rounded-xl text-xs font-semibold hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {deleting ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Attendance summary */}
          {records.length > 0 && (
            <div className="bg-nav-hover rounded-2xl p-4">
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wide mb-3">Attendance Summary</h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-card rounded-xl p-3">
                  <p className="text-2xl font-bold text-green-500">{present}</p>
                  <p className="text-xs text-subtle mt-0.5">Present</p>
                </div>
                <div className="bg-card rounded-xl p-3">
                  <p className="text-2xl font-bold text-orange-400">{late}</p>
                  <p className="text-xs text-subtle mt-0.5">Late</p>
                </div>
                <div className="bg-card rounded-xl p-3">
                  <p className="text-2xl font-bold text-red-400">{absent}</p>
                  <p className="text-xs text-subtle mt-0.5">Absent</p>
                </div>
              </div>
            </div>
          )}

          {/* Attendance list */}
          {isLoading && <p className="text-sm text-subtle text-center py-4">Loading…</p>}
          {!isLoading && records.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wide">{v.members}</h3>
              {records.map(r => (
                <div key={r._id} className="flex items-center gap-3 p-3 bg-nav-hover rounded-xl">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0
                    ${r.status === 'late'    ? 'bg-amber-500/15 text-amber-400'
                    : r.status === 'absent'  ? 'bg-red-100 text-red-400'
                    : 'bg-green-100 text-green-600'}`}>
                    {(r.studentId?.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-secondary truncate">{r.studentId?.name || 'Unknown'}</p>
                    <span className="flex items-center gap-0.5 text-xs text-subtle">
                      <MdAccessTime size={11} /> {fmtClock(r.markedAt)}
                    </span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                    ${r.status === 'late'    ? 'bg-amber-500/15 text-amber-400'
                    : r.status === 'absent'  ? 'bg-red-50 text-red-400'
                    : 'bg-green-50 text-green-600'}`}>
                    {r.status === 'late' ? 'Late' : r.status === 'absent' ? 'Absent' : 'Present'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main Sessions Page ────────────────────────────────────────────────────────
export default function Sessions({ trainerOnly = false }) {
  document.title = 'Sessions — TrainAxis';
  const { isReadOnly } = useImpersonation();
  const currentTheme = useTheme()?.currentTheme;
  const { user }  = useAuth();
  const isAdmin   = user?.role === 'admin';
  const qc        = useQueryClient();

  const [viewMode,          setViewMode]          = useState('list');
  const [dateFilter,        setDateFilter]        = useState(TODAY);
  const [classFilter,       setClassFilter]       = useState('');
  const [showCreate,        setShowCreate]        = useState(false);
  const [selected,          setSelected]          = useState(null);
  const [substituteFor,     setSubstituteFor]     = useState(null);
  const [attendingSession,  setAttendingSession]  = useState(null);
  const [editingSession,    setEditingSession]    = useState(null);

  const { data: classesData } = useQuery({
    queryKey: ['classes-list'],
    queryFn:  () => getClasses({ limit: 100 }).then(r => r.data),
    staleTime: 60000,
  });
  const classes = classesData?.classes || [];

  const { data, isLoading, error } = useQuery({
    queryKey: ['sessions', dateFilter],
    queryFn:  () => sessionService.getSessions({ date: dateFilter || undefined }),
    enabled: viewMode === 'list',
  });

  // The day's sessions — class filter applied client-side (never required to see
  // sessions), sorted by start time for a clean agenda.
  const sessions = (data?.sessions || [])
    .filter(s => !classFilter || (s.classId?._id || s.classId)?.toString() === classFilter)
    .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));

  const handleCreated = (session) => {
    setShowCreate(false);
    qc.invalidateQueries(['sessions']);
    setSelected(session);
  };

  const handleRefresh = () => {
    qc.invalidateQueries(['sessions']);
    setSelected(prev => sessions.find(s => s._id === prev?._id) || prev);
  };

  // Day navigator — selected day stays in AD ISO; shown BS-primary with an AD subline.
  const navBs           = adToBs(new Date(dateFilter + 'T00:00:00'));
  const bsPrimary       = `${navBs.monthName} ${navBs.day}, ${navBs.year}`;
  const adSubline       = new Date(dateFilter + 'T00:00:00')
    .toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
  const isTodaySelected = dateFilter === TODAY;
  const canPrev         = !trainerOnly;        // trainers stay locked to today
  const canNext         = dateFilter < TODAY;  // no future days
  const shiftDay = (delta) => {
    const d = new Date(dateFilter + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    const iso = toISO(d);
    if (iso > TODAY) return;
    if (trainerOnly && iso < TODAY) return;
    setDateFilter(iso);
  };

  // Club access blocked (subscription suspended/expired) — clear panel, not empty list.
  if (isClubSuspended(error)) return <SuspendedAccessPanel />;
  if (isClubCancelled(error)) return <SuspendedAccessPanel cancelled />;

  return (
    <div className="max-w-3xl mx-auto">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-secondary">Sessions</h1>
          <p className="text-sm text-subtle mt-0.5">
            {trainerOnly ? 'Your day — take attendance' : 'Browse a day or month and take attendance'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center bg-nav-hover rounded-xl p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                ${viewMode === 'list' ? 'bg-secondary text-card shadow-sm' : 'text-muted hover:text-secondary'}`}
            >
              <MdViewList size={15} /> List
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                ${viewMode === 'calendar' ? 'bg-secondary text-card shadow-sm' : 'text-muted hover:text-secondary'}`}
            >
              <MdCalendarViewMonth size={15} /> Calendar
            </button>
          </div>
          {isAdmin && viewMode === 'list' && (
            <button
              onClick={() => setShowCreate(true)}
              disabled={isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
              className={`flex items-center gap-2 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} px-4 py-2.5 rounded-full text-sm font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <MdAdd size={18} /> New Session
            </button>
          )}
        </div>
      </div>

      {/* ── Class filter (filters the day / month by class) ── */}
      <div className="flex justify-end mb-4">
        <div className="relative">
          <MdFilterList className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" size={16} />
          <select
            value={classFilter}
            onChange={e => setClassFilter(e.target.value)}
            className="appearance-none pl-9 pr-9 py-2 rounded-full border border-divider bg-card text-sm text-secondary focus:outline-none focus:border-secondary transition-colors"
          >
            <option value="">All classes</option>
            {classes.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
          <MdChevronRight className="absolute right-2 top-1/2 -translate-y-1/2 rotate-90 text-muted pointer-events-none" size={16} />
        </div>
      </div>

      {/* ── Calendar View ── */}
      {viewMode === 'calendar' && (
        <CalendarView
          classFilter={classFilter}
          onPickDay={(iso) => { setDateFilter(iso); setViewMode('list'); }}
        />
      )}

      {/* ── List View (day agenda) ── */}
      {viewMode === 'list' && (
        <div className="rounded-lg bg-card border border-divider p-4">
          {/* Day navigator */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => shiftDay(-1)}
              disabled={!canPrev}
              aria-label="Previous day"
              className="w-8 h-8 rounded-full border border-divider bg-card text-secondary hover:bg-secondary hover:text-card hover:border-secondary disabled:opacity-30 disabled:hover:bg-card disabled:hover:text-secondary disabled:hover:border-divider flex items-center justify-center transition-all duration-200"
            >
              <MdChevronLeft size={18} />
            </button>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <h2 className="text-base font-semibold text-secondary leading-tight">{bsPrimary}</h2>
                {isTodaySelected && (
                  <span className="text-[10px] font-semibold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">Today</span>
                )}
              </div>
              <p className="text-[11px] text-muted mt-0.5">{adSubline}</p>
            </div>
            <button
              onClick={() => shiftDay(1)}
              disabled={!canNext}
              aria-label="Next day"
              className="w-8 h-8 rounded-full border border-divider bg-card text-secondary hover:bg-secondary hover:text-card hover:border-secondary disabled:opacity-30 disabled:hover:bg-card disabled:hover:text-secondary disabled:hover:border-divider flex items-center justify-center transition-all duration-200"
            >
              <MdChevronRight size={18} />
            </button>
          </div>

          {isLoading ? (
            /* Loading */
            <div className="space-y-2.5">
              {[1, 2, 3].map(i => <div key={i} className="h-16 bg-card rounded-xl animate-pulse" />)}
            </div>
          ) : sessions.length === 0 ? (
            /* Empty state */
            <div className="text-center py-14">
              <MdPeople size={40} className="mx-auto text-subtle mb-3" />
              <p className="text-secondary font-medium">No sessions on this day</p>
              <p className="text-muted text-sm mt-1">
                {trainerOnly ? 'Nothing scheduled for you today.' : `Nothing scheduled for ${bsPrimary}.`}
              </p>
              {isAdmin && (
                <button
                  onClick={() => setShowCreate(true)}
                  disabled={isReadOnly}
                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  className={`mt-4 inline-flex items-center gap-2 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} px-4 py-2 rounded-full text-sm font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <MdAdd size={16} /> New session
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-2.5">
                {sessions.map(s => {
                  const { hm, ap } = splitTime(s.startTime);
                  const trainerLabel = !trainerOnly ? (s.substitutedBy?.name || s.trainerId?.name) : null;
                  const secondary = [
                    trainerLabel,
                    s.classId?.name,
                    s.studentCount != null ? `${s.attendanceCount ?? 0}/${s.studentCount}` : null,
                  ].filter(Boolean).join('  ·  ');
                  const cancelled = s.status === 'cancelled';
                  const taken     = s.attendanceTaken;
                  const upcoming  = s.upcoming; // future scheduled class — virtual, not yet a real session

                  return (
                    <div
                      key={s._id}
                      onClick={upcoming ? undefined : () => setSelected(s)}
                      className={`bg-card border border-divider rounded-xl p-3.5 flex items-center gap-3 transition-colors
                        ${upcoming ? '' : 'cursor-pointer hover:bg-nav-hover'}
                        ${cancelled ? 'opacity-60' : ''}`}
                    >
                      {/* Time block */}
                      <div className="flex flex-col items-center justify-center w-12 flex-shrink-0">
                        <span className="text-secondary text-base font-semibold leading-none">{hm}</span>
                        {ap && <span className="text-muted text-[10px] mt-0.5">{ap}</span>}
                      </div>

                      {/* Divider */}
                      <div className="w-px self-stretch bg-divider" />

                      {/* Middle */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-secondary font-medium truncate ${cancelled ? 'line-through' : ''}`}>{s.className}</p>
                        {secondary && <p className="text-muted text-xs truncate mt-0.5">{secondary}</p>}
                      </div>

                      {/* Right: status chip + action */}
                      <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        {upcoming ? (
                          <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-1 rounded-md">Upcoming</span>
                        ) : (
                          <>
                            {cancelled ? (
                              <span className="text-muted bg-nav-hover text-xs font-medium px-2 py-1 rounded-md">Cancelled</span>
                            ) : taken ? (
                              <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded-md flex items-center gap-1">
                                <MdCheckCircle size={12} /> Done
                              </span>
                            ) : (
                              <span className="bg-amber-500/15 text-amber-400 text-xs font-medium px-2 py-1 rounded-md">Pending</span>
                            )}
                            {(cancelled || taken) ? (
                              <button
                                onClick={() => setSelected(s)}
                                className="text-xs px-3 py-1.5 rounded-full bg-card text-secondary border border-divider hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200"
                              >
                                View
                              </button>
                            ) : (
                              <button
                                onClick={() => setAttendingSession(s)}
                                disabled={isReadOnly}
                                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                                className={`text-xs px-3 py-1.5 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                Take attendance
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* New session for this day */}
              {isAdmin && (
                <button
                  onClick={() => setShowCreate(true)}
                  disabled={isReadOnly}
                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  className="mt-2.5 w-full border border-dashed border-divider rounded-xl py-3 text-sm text-muted hover:text-secondary hover:border-divider transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MdAdd size={16} /> New session for this day
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Modals ── */}
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
          defaultClassId={classFilter}
          defaultDate={dateFilter}
        />
      )}

      {selected && (
        <SessionDetail
          session={selected}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onRefresh={handleRefresh}
          onSubstitute={() => setSubstituteFor(selected)}
          onTakeAttendance={() => { setAttendingSession(selected); setSelected(null); }}
          onEdit={() => setEditingSession(selected)}
        />
      )}

      {substituteFor && (
        <SubstituteModal
          session={substituteFor}
          onClose={() => setSubstituteFor(null)}
          onDone={() => {
            qc.invalidateQueries(['sessions']);
            if (selected?._id === substituteFor._id) handleRefresh();
          }}
        />
      )}

      {attendingSession && (
        <AttendanceModal
          session={attendingSession}
          onClose={() => setAttendingSession(null)}
          onSaved={() => {
            setAttendingSession(null);
            qc.invalidateQueries(['sessions']);
            qc.invalidateQueries(['calendar-sessions']);
            qc.invalidateQueries(['att-summary']);
          }}
        />
      )}

      {editingSession && (
        <EditModal
          session={editingSession}
          onClose={() => setEditingSession(null)}
          onSaved={() => {
            setEditingSession(null);
            setSelected(null);
            qc.invalidateQueries(['sessions']);
            qc.invalidateQueries(['calendar-sessions']);
          }}
        />
      )}
    </div>
  );
}
